import type { AuthUser, HoldReason, LoginResponse, SessionState, TenantBranding } from "@match/shared";
import type {
  Account,
  AccountGroup,
  AuditRow,
  BrokerServer,
  BaseSource,
  Candles,
  CashScanOutcome,
  ClientAccessCreated,
  ClientAccount,
  ClientDetail,
  ClientListItem,
  DeletedRoots,
  DeletionPlan,
  CompareResult,
  ComputerIdChange,
  ComputerIdChanged,
  ConnectedCount,
  CycleAwaitingAccount,
  CycleDetail,
  CycleListItem,
  CyclePeriods,
  PayoutPreview,
  NotificationSettings,
  HedgePair,
  Ledger,
  LedgerRow,
  ModifyLevelsResult,
  RealignResult,
  Reconciliation,
  RiskEventRow,
  TransactionKind,
  Preview,
  PropFirm,
  ProxyCheck,
  ProxyRow,
  SessionAwaitingProp,
  SessionDetail,
  SessionListItem,
  SessionSymbols,
  Preset,
  PresetInput,
  CreatableRole,
  CyclePayout,
  ServerChangePreview,
  UserRow,
  SymbolCandidates,
  SymbolMapping,
  ValuesInUse,
} from "@/lib/types";

const ACCESS_KEY = "match.access";
const REFRESH_KEY = "match.refresh";

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh?: string) {
    localStorage.setItem(ACCESS_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /**
     * Il `code` del backend, quando c'e'. Serve a dire una frase DIVERSA dove
     * il motivo del rifiuto cambia cosa deve fare chi legge: riprovare non e'
     * correggere un campo. Senza, l'unico appiglio sarebbe il testo del
     * messaggio, che cambia senza che niente diventi rosso.
     */
    readonly code: string | null = null,
    /**
     * Fino a 5 nomi simili, quando il backend li manda (`SERVER_NOT_ON_GATEWAY`):
     * il form li propone cliccabili invece di far ridigitare il nome a caso.
     */
    readonly suggestions: string[] | null = null,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}, retry = true): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const res = await fetch(`/api${path}`, {
    ...init,
    // Fastify rifiuta un Content-Type JSON con corpo vuoto: le azioni senza
    // dati (connetti, archivia) mandano un oggetto vuoto.
    body: init.body ?? (method === "GET" ? undefined : "{}"),
    headers: {
      "Content-Type": "application/json",
      ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
      ...init.headers,
    },
  });

  // Access token scaduto: un solo tentativo di refresh, poi si torna al login.
  if (res.status === 401 && retry && tokens.refresh) {
    const refreshed = await fetch("/api/auth/refresh", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: tokens.refresh }),
    });
    if (refreshed.ok) {
      const { accessToken } = (await refreshed.json()) as { accessToken: string };
      tokens.set(accessToken);
      return request<T>(path, init, false);
    }
    tokens.clear();
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      code?: string;
      fields?: { path: string; message: string }[];
      suggestions?: string[];
    };
    // Sugli errori di validazione il backend dice quale campo: lo si mostra.
    const detail = body.fields?.length ? `: ${body.fields.map((f) => `${f.path} ${f.message}`).join(", ")}` : "";
    throw new ApiError((body.error ?? `Errore ${res.status}`) + detail, res.status, body.code ?? null, body.suggestions ?? null);
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

const post = <T>(path: string, body?: unknown) =>
  request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) });
const patch = <T>(path: string, body: unknown) => request<T>(path, { method: "PATCH", body: JSON.stringify(body) });
const del = <T>(path: string) => request<T>(path, { method: "DELETE" });

export interface ClientInput {
  name: string;
  email: string;
  notes?: string | null;
  computerId?: string | null;
  defaultProxyId?: string | null;
  /**
   * L'utente del cliente, solo alla creazione. Nessuna email: prende sempre
   * quella del cliente (`email`, sopra), il backend la ignorerebbe comunque
   * se ce ne fosse una. La password va GENERATA: il backend ne salva l'hash
   * argon2id e non c'e' modo di rileggerla.
   */
  access?: { password: string };
}

export interface AccountInput {
  label: string;
  clientId: string;
  role: "PROP" | "BROKER" | "NONE";
  mt5Login: string;
  mt5Password: string;
  serverId: string;
  currency: string;
  computerId?: string | null;
  proxyId?: string | null;
  notes?: string | null;
  /** Stima facoltativa: vuota va bene, la impara il consuntivo. */
  commissionPerLot?: string | null;
  commissionCharge?: "OPEN" | "SPLIT" | "CLOSE" | null;
}

/**
 * Login, server e password sono l'identita' del conto anche su Mirror — e
 * proprio per questo si POSSONO correggere da qui: un login scritto sbagliato
 * o un conto spostato su un altro server. Su un conto connesso il backend
 * disconnette, salva e riconnette da solo; con posizioni a mercato rifiuta.
 */
export type AccountPatch = Partial<Omit<AccountInput, "clientId">> & {
  leverageOverride?: number | null;
  ledgerStartBalance?: string | null;
  ledgerStartAt?: string | null;
};

export interface TransactionInput {
  clientId: string;
  kind: TransactionKind;
  /** Con segno: il backend controlla il segno per tipo. */
  amount: string;
  currency?: string;
  accountId?: string | null;
  cycleId?: string | null;
  occurredAt: string;
  reference?: string | null;
  notes?: string | null;
  fxRate?: string;
}

/** I campi di una riga del registro che si correggono: gli altri sono un altro movimento. */
export interface LedgerChangeInput {
  reason: string;
  amount?: string;
  occurredAt?: string;
  reference?: string | null;
  notes?: string | null;
  /** Le due quote di uno split, positive: si muovono insieme e la somma non cambia. */
  split?: { client: string; manager: string };
}

/** I numeri del ciclo che una riga muove. `null` su una riga senza ciclo. */
export interface CacheCiclo {
  currency: string;
  feesPaid: number;
  payoutsReceived: number;
  adjustments: number;
  realRealizedPnl: number;
  recoverAmount: number;
}

export interface LedgerChange {
  row: LedgerRow | null;
  sibling: LedgerRow | null;
  cycleId: string | null;
  before: CacheCiclo | null;
  after: CacheCiclo | null;
}

export interface ProxyInput {
  label?: string | null;
  provider?: string | null;
  host: string;
  port: number;
  type: "SOCKS5" | "HTTP" | "HTTPS";
  username?: string | null;
  /** Solo il segreto: i parametri sotto viaggiano separati. */
  password?: string | null;
  country?: string | null;
  city?: string | null;
  /**
   * `sessionId` NON c'e', ed e' voluto: si deriva dal cliente alla creazione e
   * non si modifica. E' l'identificativo che decide da che indirizzo esce quel
   * cliente, non una preferenza da riempire in un form.
   */
  notes?: string | null;
  isActive?: boolean;
}

/** `PATCH /servers/:id`: nome, nome visualizzato, possessore. Il tipo e il fuso non si cambiano. */
export interface ServerPatch {
  name?: string;
  displayName?: string | null;
  propFirmId?: string | null;
}

/** `PATCH /prop-firms/:id`: il tipo (PROP o BROKER) non si cambia. */
export interface PropFirmPatch {
  name?: string;
  websiteUrl?: string | null;
  externalHedgingAllowed?: boolean;
  hedgingRuleNotes?: string | null;
}

export interface ServerInput {
  name: string;
  kind: "PROP" | "BROKER";
  /**
   * Nessun fuso: lo legge il gateway alla prima connessione di un conto di
   * questo server. Era una tenda di sei voci, e sbagliarla spostava la
   * mezzanotte del DD giornaliero senza che nessuno potesse accorgersene.
   */
  propFirmId?: string | null;
}

export interface RiskChecksHealth {
  enabled: boolean;
  intervalMs: number;
  lastRunAt: string | null;
  ageMs: number | null;
  stale: boolean;
}

export const api = {
  branding: () => request<TenantBranding>("/tenant/branding"),

  /**
   * Lo stato delle guardie. **Sta fuori da `/api`**, come `/health/mirror`:
   * e' la rotta che dice se il gestionale sta guardando, e non deve dipendere
   * da niente che possa essere rotto.
   */
  health: async () => {
    const res = await fetch("/health");
    return (await res.json()) as { ok: boolean; at: string; riskChecks: RiskChecksHealth };
  },

  /**
   * L'area cliente: una rotta sua, non una vista ridotta di `/accounts`.
   *
   * Sette campi e basta — niente credenziali, niente proxy, niente CID: il
   * cliente non deve sapere di AVERE un proxy, perché il proxy dedicato è
   * l'architettura anti-correlazione, cioè la cosa che gli vendiamo.
   *
   * Si chiama `myAccounts` e non `me.accounts` perché `me()` è già l'utente
   * corrente (`/auth/me`): due cose diverse con lo stesso nome finiscono per
   * essere chiamate l'una al posto dell'altra.
   */
  myAccounts: () => request<{ accounts: ClientAccount[] }>("/me/accounts"),

  /**
   * L'anteprima e la cancellazione, per URL.
   *
   * Un metodo per entità moltiplicherebbe cinque righe uguali; il percorso lo
   * costruisce `DeleteForever`, che è l'unico posto dove i due URL devono
   * parlare della stessa riga.
   */
  deletionPreview: <T = DeletionPlan>(url: string) => request<T>(url),
  deleteAt: <T = { ok: true }>(url: string) => del<T>(url),

  /** Le radici eliminate (conti, cicli, fasi) e il ripristino di una di esse. */
  deleted: () => request<DeletedRoots>("/deleted"),
  undelete: (kind: "accounts" | "cycles" | "sessions", id: string) => post<{ ok: true }>(`/${kind}/${id}/undelete`),

  /** Quello che è archiviato, e che da lì può tornare attivo. */
  archived: () =>
    request<{
      clients: { id: string; name: string; email: string; updatedAt: string }[];
      accounts: {
        id: string;
        label: string;
        role: "PROP" | "BROKER";
        mt5Login: string;
        currency: string;
        updatedAt: string;
        server: { name: string };
        client: { id: string; name: string; isArchived: boolean };
      }[];
    }>("/archived"),

  login: (email: string, password: string) =>
    request<LoginResponse>("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),

  me: () => request<AuthUser>("/auth/me"),

  /**
   * Cambia la propria password. Restituisce token NUOVI, e vanno salvati:
   * `mustChangePassword` viaggia dentro il token, quindi quello di prima
   * direbbe ancora «deve cambiarla».
   */
  changePassword: (current: string, next: string) =>
    post<LoginResponse>("/auth/change-password", { current, next }),

  logout: async () => {
    await request("/auth/logout", {
      method: "POST",
      body: JSON.stringify({ refreshToken: tokens.refresh }),
    }).catch(() => undefined);
    tokens.clear();
  },

  clients: {
    list: () => request<ClientListItem[]>("/clients"),
    get: (id: string) => request<ClientDetail>(`/clients/${id}`),
    create: (body: ClientInput) => post<ClientListItem>("/clients", body),
    update: (id: string, body: Partial<ClientInput>) => patch<ClientListItem>(`/clients/${id}`, body),
    /**
     * Cambia il CID: rotta sua, solo ADMIN, e non un campo del `PATCH`.
     * Rifiutata con `CLIENT_ACCOUNTS_CONNECTED` finche' un conto e' connesso.
     */
    setComputerId: (id: string, body: ComputerIdChange) => post<ComputerIdChanged>(`/clients/${id}/computer-id`, body),
    /**
     * Da' l'accesso a un cliente che esiste gia'. Solo ADMIN, e la password
     * torna **una volta sola**: nel database c'e' l'hash.
     *
     * Nessun campo: l'email e' sempre quella dell'anagrafica. Rifiutata con
     * `EMAIL_TAKEN` se quell'email ha gia' un accesso in un tenant qualunque,
     * con `ACCESS_EXISTS` se questo cliente ce l'ha gia'.
     */
    createAccess: (id: string) => post<ClientAccessCreated>(`/clients/${id}/access`),
    archive: (id: string) => del<{ ok: true }>(`/clients/${id}`),
    restore: (id: string) => post<{ ok: true }>(`/clients/${id}/restore`),
    deletionPreview: (id: string) => request<DeletionPlan>(`/clients/${id}/deletion-preview`),
    deleteForever: (id: string) => del<{ ok: true }>(`/clients/${id}/permanent`),
  },

  /**
   * GLI ACCESSI, e non c'e' una pagina Utenti: da qui li usa la scheda del
   * cliente.
   *
   * Sono le rotte di `routes/users.ts`, **le stesse per chiunque**: disattivare
   * l'accesso di un cliente e disattivare quello di un collega sono la stessa
   * azione sulla stessa riga, con lo stesso audit. Un paio di rotte gemelle
   * sotto `/clients` sarebbe una seconda implementazione da tenere allineata.
   *
   * Solo ADMIN, come la creazione: le maniglie si mostrano solo a lui.
   */
  users: {
    list: () => request<UserRow[]>("/users"),
    /** Crea un utente interno (mai un ADMIN). La password torna UNA volta sola. */
    create: (body: { email: string; fullName?: string | null; role: CreatableRole }) =>
      post<UserRow & { password: string }>("/users", body),
    /** Disattiva o riattiva un accesso. Un utente disattivato non entra piu'. */
    setActive: (id: string, isActive: boolean) =>
      patch<{ id: string; email: string; isActive: boolean }>(`/users/${id}`, { isActive }),
    /**
     * Genera una password nuova, la restituisce **una volta sola** e butta i
     * refresh token: chi era entrato prima resta fuori.
     */
    resetPassword: (id: string) => post<{ ok: true; email: string; password: string }>(`/users/${id}/reset-password`),
  },

  accounts: {
    list: () => request<AccountGroup[]>("/accounts"),
    create: (body: AccountInput) => post<Account>("/accounts", body),
    /**
     * `reconnectError`: valorizzato solo quando login/server/password sono
     * cambiati su un conto connesso e la riconnessione dopo il salvataggio
     * NON e' riuscita — il salvataggio resta comunque valido, si dichiara
     * solo che il conto è tornato disconnesso.
     */
    update: (id: string, body: AccountPatch) => patch<Account & { reconnectError: string | null }>(`/accounts/${id}`, body),
    archive: (id: string) => del<{ ok: true }>(`/accounts/${id}`),
    restore: (id: string) => post<{ ok: true }>(`/accounts/${id}/restore`),
    deletionPreview: (id: string) => request<DeletionPlan>(`/accounts/${id}/deletion-preview`),
    deleteForever: (id: string) => del<{ ok: true }>(`/accounts/${id}/permanent`),
    /**
     * Rilegge la password MT5 di un conto. `POST` perché un GET finisce nella
     * cronologia e lo può far partire un link; tutti gli interni, il CLIENT
     * mai; e ogni chiamata
     * lascia una riga di audit, che è il motivo per cui non si mette in cache:
     * il valore si guarda adesso, e per riguardarlo si richiede.
     */
    revealPassword: (id: string) => post<{ password: string }>(`/accounts/${id}/reveal-password`),
    connect: (id: string) => post<Account>(`/accounts/${id}/connect`),
    disconnect: (id: string) => post<Account>(`/accounts/${id}/disconnect`),
    sync: (id: string) => post<Account>(`/accounts/${id}/sync`),
    /** Quanti conti sono connessi adesso: con un gateway che fattura sulle connessioni, e' un costo. */
    connectedCount: () => request<ConnectedCount>("/accounts/connected-count"),
  },

  proxies: {
    list: () => request<ProxyRow[]>("/proxies"),
    /** `clientId` obbligatorio: un proxy nasce PER un cliente, e da li' viene la sessione. */
    create: (body: ProxyInput & { clientId: string }) => post<ProxyRow>("/proxies", body),
    update: (id: string, body: Partial<ProxyInput>) => patch<ProxyRow>(`/proxies/${id}`, body),
    remove: (id: string) => del<{ ok: true }>(`/proxies/${id}`),
    check: (id: string) => post<ProxyRow & { check: ProxyCheck }>(`/proxies/${id}/check`),
  },

  servers: {
    list: () => request<BrokerServer[]>("/servers"),
    create: (body: ServerInput) => post<BrokerServer>("/servers", body),
    update: (id: string, body: ServerPatch) => patch<BrokerServer>(`/servers/${id}`, body),
    /** Le conseguenze di un nuovo nome, prima di salvarlo. */
    changePreview: (id: string, name: string) =>
      request<ServerChangePreview>(`/servers/${id}/change-preview?name=${encodeURIComponent(name)}`),
    addSymbol: (serverId: string, body: SymbolInput) => post<SymbolMapping>(`/servers/${serverId}/symbols`, body),
    updateSymbol: (serverId: string, symbolId: string, body: Partial<SymbolInput>) =>
      patch<SymbolMapping>(`/servers/${serverId}/symbols/${symbolId}`, body),
    /** I simboli che il server ha davvero, letti da un conto collegato. */
    candidates: (serverId: string, canonical: string) =>
      request<SymbolCandidates>(`/servers/${serverId}/symbols/candidates?canonical=${encodeURIComponent(canonical)}`),
    /** Scrive la mappatura dal candidato scelto: si manda il NOME, i numeri li rilegge il backend. */
    syncSymbol: (serverId: string, body: { canonical: string; brokerSymbol: string }) =>
      post<{ symbol: SymbolMapping; verified: boolean; blocker: string | null; changed: string[] }>(`/servers/${serverId}/symbols/sync`, body),
  },

  propFirms: {
    /** Si ARCHIVIANO, non si cancellano: un preset serve a rileggere i cicli che l'hanno usato. */
    archive: (id: string) => del<{ ok: true; presetsArchiviati: number }>(`/prop-firms/${id}`),
    restore: (id: string) => post<{ ok: true }>(`/prop-firms/${id}/restore`),
    archivePreset: (id: string) => del<{ ok: true; cicliCheLoUsano: number }>(`/presets/${id}`),
    restorePreset: (id: string) => post<{ ok: true }>(`/presets/${id}/restore`),
    /** Senza `kind` sono le prop firm; `"BROKER"` elenca i broker (stessa tabella, stesso modo di sceglierli). */
    list: (kind?: "PROP" | "BROKER") => request<PropFirm[]>(kind ? `/prop-firms?kind=${kind}` : "/prop-firms"),
    /** Gli archiviati: firm archiviate coi loro programmi, e firm attive coi soli programmi archiviati. */
    listArchived: (kind: "PROP" | "BROKER") => request<PropFirm[]>(`/prop-firms?kind=${kind}&archived=true`),
    create: (body: { name: string; kind?: "PROP" | "BROKER"; websiteUrl?: string | null; externalHedgingAllowed?: boolean; hedgingRuleNotes?: string | null }) =>
      post<PropFirm>("/prop-firms", body),
    createPreset: (firmId: string, body: PresetInput) => post<Preset>(`/prop-firms/${firmId}/presets`, body),
    update: (id: string, body: PropFirmPatch) => patch<PropFirm>(`/prop-firms/${id}`, body),
    updatePreset: (presetId: string, body: Partial<PresetInput> & { isArchived?: boolean }) =>
      patch<Preset>(`/presets/${presetId}`, body),
  },

  sessions: {
    list: (q: { state?: string[]; clientId?: string; phase?: string; hasCycle?: boolean } = {}) => {
      const p = new URLSearchParams();
      if (q.state?.length) p.set("state", q.state.join(","));
      if (q.clientId) p.set("clientId", q.clientId);
      if (q.phase) p.set("phase", q.phase);
      if (q.hasCycle !== undefined) p.set("hasCycle", String(q.hasCycle));
      const qs = p.toString();
      return request<SessionListItem[]>(`/sessions${qs ? `?${qs}` : ""}`);
    },
    /** Le Fasi SENZA ciclo che aspettano il conto prop della fase nuova, dalla più vecchia. */
    awaitingProp: (q: { clientId?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.clientId) p.set("clientId", q.clientId);
      const qs = p.toString();
      return request<SessionAwaitingProp[]>(`/sessions/awaiting-prop${qs ? `?${qs}` : ""}`);
    },
    /**
     * IL CONTO PROP E' ARRIVATO per una Fase senza ciclo: nasce il conto e con
     * lui la Fase successiva (la stessa fase se il conto era bruciato). Il
     * broker è quello di prima, proposto, salvo scelta esplicita.
     */
    nextAccount: (
      sessionId: string,
      body: AccountChoice & { brokerAccountId?: string; newBroker?: { mt5Login: string; mt5Password: string; serverId: string; label?: string | null; currency: string }; noBroker?: true },
    ) => post<{ session: { id: string; state: SessionState }; period: null }>(`/sessions/${sessionId}/next-account`, body),
    /**
     * «CREA CICLO»: da qui la Fase ha una contabilità. Solo da una Fase 1; il
     * saldo del broker si legge al momento, e senza non nasce niente.
     */
    createCycle: (sessionId: string, body: { cycleLabel?: string | null; challengeFee?: string; challengeFeeCurrency?: string; challengeFeeFxRate?: string } = {}) =>
      post<{ cycle: { id: string }; session: { id: string }; period: unknown }>(`/sessions/${sessionId}/create-cycle`, body),
    get: (id: string) => request<SessionDetail>(`/sessions/${id}`),
    symbols: (id: string) => request<SessionSymbols>(`/sessions/${id}/symbols`),
    create: (body: SessionInput) =>
      post<{ session: { id: string; state: SessionState; brokerAccountId: string | null }; cycleCreated: boolean }>("/sessions", body),
    /**
     * IL BROKER ARRIVA DOPO: aggancia un conto a una Fase `AWAITING_BROKER`.
     * Un gesto che succede una volta sola — da qui in poi la Fase si comporta
     * come una nata con broker fin dall'inizio.
     */
    attachBroker: (id: string, body: AttachBrokerInput) =>
      post<{ session: { id: string; state: SessionState }; period: unknown }>(`/sessions/${id}/attach-broker`, body),
    update: (id: string, body: Partial<SessionParamsInput>) => patch<unknown>(`/sessions/${id}`, body),
    /**
     * FERMA LA FASE, col motivo — obbligatorio, e con `ALTRO` anche il testo.
     *
     * Non c'e' una pausa senza motivo: una Fase ferma e basta e' identica a una
     * dimenticata, ed e' quel caso a costare. Il backend rifiuta con 400.
     */
    pause: (id: string, body: { reason: HoldReason; note?: string }) => post<unknown>(`/sessions/${id}/pause`, body),
    resume: (id: string) => post<unknown>(`/sessions/${id}/resume`),
    complete: (id: string, body: OutcomeInput) => post<unknown>(`/sessions/${id}/complete`, body),
    fail: (id: string, body: OutcomeInput) => post<unknown>(`/sessions/${id}/fail`, body),
    /** Netto del ciclo e quote, prima di confermare la bruciatura. Non scrive. */
    burnPreview: (id: string, body: { cycleAdjustment?: string; fxRate?: string } = {}) =>
      post<BurnPreview>(`/sessions/${id}/burn-preview`, body),
    /**
     * «Ho chiesto il payout»: chiude il tratto e mette la Fase in attesa.
     * Nessun importo — non si sa ancora quanto arriverà — e nessuna riga nel
     * registro: quello che fissa è il confine.
     */
    awaitPayout: (id: string, body: { fxRate?: string; note?: string } = {}) =>
      post<{ state: "AWAITING_PAYOUT"; period: { seq: number; netNative: number; netCycle: number; currency: string; cycleCurrency: string } }>(
        `/sessions/${id}/await-payout`,
        body,
      ),
    /** Le due quote e la base del tratto nuovo, prima di registrare. Non scrive. */
    payoutPreview: (id: string, netAmount: string) => post<PayoutPreview>(`/sessions/${id}/payout-preview`, { netAmount }),
    /** «Il payout è arrivato»: incasso, quote e tratto nuovo, in una transazione. */
    payoutReceived: (id: string, body: PayoutReceivedInput) => post<{ state: "ACTIVE" }>(`/sessions/${id}/payout-received`, body),
    /**
     * Rileva adesso i movimenti di cassa del conto broker. Serve quando si SA
     * che è arrivato un versamento e si vuole vedere la base muoversi senza
     * aspettare la prossima chiusura.
     */
    cashScan: (id: string) => post<CashScanOutcome>(`/sessions/${id}/cash-scan`),
    draftPreview: (body: DraftPreviewInput) => post<Preview>("/sessions/preview", body),
    /** Lotto e moltiplicatore sono obbligatori: la fase non li tiene piu'. */
    preview: (
      id: string,
      q: { direction: "LONG" | "SHORT"; lots: string; multiplier: string; canonicalSymbol?: string; slMoney?: string; tpMoney?: string; sl?: number; tp?: number; targetNetGain?: string },
    ) => {
      const p = new URLSearchParams({ direction: q.direction, lots: q.lots, multiplier: q.multiplier });
      // SL e TP in denaro, nella valuta del conto prop; in alternativa i punti.
      if (q.canonicalSymbol) p.set("canonicalSymbol", q.canonicalSymbol);
      if (q.slMoney) p.set("slMoney", q.slMoney);
      if (q.tpMoney) p.set("tpMoney", q.tpMoney);
      if (q.sl !== undefined) p.set("sl", String(q.sl));
      if (q.tp !== undefined) p.set("tp", String(q.tp));
      // Calcolo inverso: netto voluto alla bruciatura -> moltiplicatore.
      if (q.targetNetGain) p.set("targetNetGain", q.targetNetGain);
      return request<Preview>(`/sessions/${id}/preview?${p.toString()}`);
    },
    openPair: (id: string, body: OperationInput) =>
      // `valuePerPoint` e' quello con cui la coppia e' stata calcolata, e in
      // apertura e' sempre letto dal broker: se non si fosse potuto leggere,
      // questa chiamata sarebbe stata rifiutata (`VALUE_PER_POINT_UNREADABLE`).
      post<{ pair: HedgePair; money: Preview["money"]; valuePerPoint: ValuesInUse; warnings: string[] }>(`/sessions/${id}/pairs`, body),
    closePair: (id: string, pairId: string) => post<HedgePair>(`/sessions/${id}/pairs/${pairId}/close`),
    /** Abbandona: NON chiude niente a mercato. Il backend rifiuta se il gateway risponde. */
    abandonPair: (id: string, pairId: string) =>
      post<{ outcome: "ABANDONED" | "CONSOLIDATED"; unknownLegs?: string[]; state?: string; closeReason?: string | null }>(
        `/sessions/${id}/pairs/${pairId}/abandon`,
      ),
    /**
     * Le candele del grafico: UNA lettura all'apertura della schermata, con la
     * coppia a mercato o senza (`pairId` nullo: solo le candele, per decidere
     * PRIMA di aprire). Se fallisce non e' un problema di rischio: si mostra
     * l'input in denaro senza grafico e si dice perche'.
     */
    candles: (id: string, pairId: string | null, q: { timeframe?: number; bars?: number; role?: "PROP" | "BROKER"; canonicalSymbol?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.timeframe) p.set("timeframe", String(q.timeframe));
      if (q.bars) p.set("bars", String(q.bars));
      if (q.role) p.set("role", q.role);
      // Senza coppia (prima di aprire) lo strumento lo dice chi chiama; con la coppia e' il suo.
      if (pairId === null && q.canonicalSymbol) p.set("canonicalSymbol", q.canonicalSymbol);
      const qs = p.toString();
      const base = pairId === null ? `/sessions/${id}/candles` : `/sessions/${id}/pairs/${pairId}/candles`;
      return request<Candles>(`${base}${qs ? `?${qs}` : ""}`);
    },
    /**
     * RIALLINEA la gamba broker sullo specchio del prop.
     *
     * Non e' `modifyLevels` con un corpo diverso: li' l'operatore chiede un
     * importo e si toccano tutt'e due le gambe, qui il bersaglio e' la gamba
     * prop com'e' adesso e **il prop non si tocca mai** — una modifica dello
     * stop pochi secondi dopo l'ingresso e' un gesto che la firm registra.
     */
    realign: (id: string, pairId: string) => post<RealignResult>(`/sessions/${id}/pairs/${pairId}/realign`, {}),
    /**
     * I livelli di una coppia aperta, in DENARO e in valuta del conto prop.
     * Tocca tutt'e due le gambe, perche' l'importo l'ha chiesto l'operatore.
     */
    modifyLevels: (id: string, pairId: string, body: { slMoney: number; tpMoney?: number }) =>
      // `valuePerPoint` qui e' sempre letto dal vivo: se non si fosse potuto
      // leggere, la modifica sarebbe stata rifiutata (`VALUE_PER_POINT_UNREADABLE`).
      // Gli importi invece ci sono solo se qualcosa e' cambiato: vedi il tipo.
      patch<ModifyLevelsResult>(`/sessions/${id}/pairs/${pairId}/levels`, body),
    adjustLeg: (id: string, legId: string, body: { pnlAdjustment: string; reason: string }) =>
      patch<unknown>(`/sessions/${id}/legs/${legId}/adjustment`, body),
  },

  /**
   * Eventi di rischio: gli aperti, lo storico, la presa in carico. E l'audit,
   * che e' un'altra tabella e un altro pubblico.
   */
  risk: {
    open: (q: { sessionId?: string; accountId?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.sessionId) p.set("sessionId", q.sessionId);
      if (q.accountId) p.set("accountId", q.accountId);
      const qs = p.toString();
      return request<{ rows: RiskEventRow[]; counts: { critical: number; warning: number } }>(`/risk-events/open${qs ? `?${qs}` : ""}`);
    },
    list: (q: { kind?: string[]; severity?: string[]; state?: "open" | "resolved"; accountId?: string; sessionId?: string; from?: string; to?: string; limit?: number; offset?: number } = {}) => {
      const p = new URLSearchParams();
      if (q.kind?.length) p.set("kind", q.kind.join(","));
      if (q.severity?.length) p.set("severity", q.severity.join(","));
      if (q.state) p.set("state", q.state);
      if (q.accountId) p.set("accountId", q.accountId);
      if (q.sessionId) p.set("sessionId", q.sessionId);
      if (q.from) p.set("from", q.from);
      if (q.to) p.set("to", q.to);
      if (q.limit !== undefined) p.set("limit", String(q.limit));
      if (q.offset) p.set("offset", String(q.offset));
      const qs = p.toString();
      return request<Paged<RiskEventRow>>(`/risk-events${qs ? `?${qs}` : ""}`);
    },
    /** «Me ne occupo io»: l'unica scrittura su un evento. */
    resolve: (id: string) => post<RiskEventRow>(`/risk-events/${id}/resolve`),
  },

  audit: (q: { entityType?: string; entityId?: string; action?: string; from?: string; to?: string; limit?: number; offset?: number } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined && v !== "") p.set(k, String(v));
    const qs = p.toString();
    return request<Paged<AuditRow> & { scope: string[] | null }>(`/audit${qs ? `?${qs}` : ""}`);
  },

  cycles: {
    list: (q: { state?: string[]; clientId?: string } = {}) => {
      const p = new URLSearchParams();
      for (const st of q.state ?? []) p.append("state", st);
      if (q.clientId) p.set("clientId", q.clientId);
      const qs = p.toString();
      return request<CycleListItem[]>(`/cycles${qs ? `?${qs}` : ""}`);
    },
    get: (id: string) => request<CycleDetail>(`/cycles/${id}`),
    /**
     * I periodi del ciclo: base, movimenti di cassa, saldo di chiusura e netto
     * nelle due valute. **Non è nell'allowlist di un CLIENT**: la sezione non
     * si mostra a lui, o sarebbe una chiamata che nasce 403.
     */
    periods: (id: string) => request<CyclePeriods>(`/cycles/${id}/periods`),
    /** I payout del ciclo, dal piu' recente. Non e' nell'allowlist del CLIENT: non si chiama per lui. */
    payouts: (id: string) => request<CyclePayout[]>(`/cycles/${id}/payouts`),
    /**
     * Corregge la base di un periodo. Il motivo è obbligatorio: è l'unica cosa
     * che, fra un anno, spiega perché il netto di quel tratto è quello.
     * Rifiutata su un ciclo chiuso (`CYCLE_CLOSED`).
     */
    setPeriodBase: (cycleId: string, periodId: string, body: { baseAmount: string; reason: string }) =>
      patch<{ period: { id: string; baseAmount: string; baseSource: BaseSource; baseNote: string | null } }>(
        `/cycles/${cycleId}/periods/${periodId}/base`,
        body,
      ),
    /** I cicli fermi in attesa del conto prop della fase nuova, dal più vecchio. */
    awaitingAccount: (q: { clientId?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.clientId) p.set("clientId", q.clientId);
      const qs = p.toString();
      return request<CycleAwaitingAccount[]>(`/cycles/awaiting-account${qs ? `?${qs}` : ""}`);
    },
    /**
     * IL CONTO E' ARRIVATO: nasce il conto prop, e con lui la Fase successiva.
     * Il broker è opzionale — nessuno dei tre campi copia quello dell'ultima
     * fase chiusa, come sempre.
     */
    nextAccount: (cycleId: string, body: AccountChoice & { brokerAccountId?: string; newBroker?: { mt5Login: string; mt5Password: string; serverId: string; label?: string | null; currency: string }; noBroker?: true }) =>
      post<{ session: { id: string; state: SessionState }; period: unknown }>(`/cycles/${cycleId}/next-account`, body),
  },

  ledger: {
    list: (clientId: string, q: { kind?: TransactionKind; from?: string; to?: string } = {}) => {
      const p = new URLSearchParams();
      if (q.kind) p.set("kind", q.kind);
      if (q.from) p.set("from", q.from);
      if (q.to) p.set("to", q.to);
      const qs = p.toString();
      return request<Ledger>(`/clients/${clientId}/transactions${qs ? `?${qs}` : ""}`);
    },
    create: (body: TransactionInput) => post<LedgerRow>("/transactions", body),
    /**
     * Modifica una riga del registro. Solo ADMIN, motivo obbligatorio, audit
     * col prima e il dopo: lo storno non c e' piu', una riga sbagliata si
     * corregge. I numeri del ciclo si rifanno nella stessa transazione, e
     * tornano qui in `before`/`after`.
     */
    update: (id: string, body: LedgerChangeInput) => patch<LedgerChange>(`/transactions/${id}`, body),
    /** Elimina una riga. Stesso giro della modifica, col suo motivo. */
    remove: (id: string, reason: string) => request<LedgerChange>(`/transactions/${id}`, { method: "DELETE", body: JSON.stringify({ reason }) }),
    /** Che cosa succede al ciclo se confermo: gli stessi numeri, senza scrivere. */
    changePreview: (id: string, body: LedgerChangeInput & { remove?: boolean }) =>
      post<LedgerChange>(`/transactions/${id}/change-preview`, body),
    reconciliation: (accountId: string) => request<Reconciliation>(`/accounts/${accountId}/reconciliation`),
    confirmReconciliation: (accountId: string, note: string | null) => post<unknown>(`/accounts/${accountId}/reconciliation/confirm`, { note }),
  },

  notifications: {
    get: () => request<NotificationSettings>("/tenant/notifications"),
    save: (body: { telegramChatId: string | null; enabled: boolean; events: string[]; dedupeWindowMinutes: number }) =>
      request<NotificationSettings>("/tenant/notifications", { method: "PUT", body: JSON.stringify(body) }),
    test: () => post<{ ok: true }>("/tenant/notifications/test"),
  },

  compare: {
    get: (from: string, to: string) => request<CompareResult>(`/compare?${new URLSearchParams({ from, to })}`),
    /** Il CSV vuole l'header di autenticazione: niente link diretto, si scarica come blob. */
    downloadCsv: async (from: string, to: string) => {
      const res = await fetch(`/api/compare?${new URLSearchParams({ from, to, format: "csv" })}`, {
        headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
      });
      if (!res.ok) throw new ApiError(`Export non riuscito (${res.status})`, res.status);
      const name = /filename="([^"]+)"/.exec(res.headers.get("Content-Disposition") ?? "")?.[1] ?? "confronto.csv";
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    },
  },

  /** Solo in sviluppo, col client Mirror finto. */
  dev: {
    movePrice: (points: number) => post<{ price: number }>("/dev/mirror/move-price", { points }),
    mirrorBootstrap: () =>
      post<{ accounts: number; positions: number; excluded: { accountId: string; label: string; mt5Login: string; reason: string }[] }>("/dev/mirror/bootstrap"),
    mirrorHealth: async () => {
      const res = await fetch("/health/mirror");
      return (await res.json()) as { reachable: boolean; version: string | null };
    },
  },
};

export interface SymbolInput {
  canonical: string;
  brokerSymbol: string;
  digits: number;
  point: string;
  valuePerPoint: string;
  lotStep: string;
  minLot: string;
  stopsLevel: number;
}

/** I limiti della fase: lotto, moltiplicatore e livelli sono dell'operazione. */
export interface SessionParamsInput {
  dailyDdPct: string | null;
  challengeTargetPct: string | null;
  /** Scarto massimo fra le gambe, in valuta prop. Null = il predefinito. */
  levelsDriftMaxMoney: string | null;
}

export interface SessionInput {
  clientId: string;
  propAccountId: string;
  /** Assente = la Fase nasce `AWAITING_BROKER`: il broker si aggancia dopo. */
  brokerAccountId?: string;
  canonicalSymbol: string;
  phase: "PHASE_1" | "PHASE_2" | "FUNDED";
  /**
   * CONTABILITÀ / CICLO. `false` = la Fase opera da sola, senza ciclo. La UI lo
   * manda sempre in modo esplicito (l'API, senza, vale `true`).
   */
  accounting?: boolean;
  /** Il ciclo a cui attaccarsi: in fase 2 e funded serve, in fase 1 e' rifiutato. */
  cycleId?: string;
  presetId?: string;
  cycleLabel?: string | null;
  accountSize?: string;
  challengeFee?: string;
  challengeFeeCurrency?: string;
  challengeFeeFxRate?: string;
  maxDdPct?: string;
  maxDdBasis?: string;
  dailyDdPct?: string | null;
  challengeTargetPct?: string | null;
  /**
   * La base del primo periodo, a mano. **Solo se il saldo non si legge**
   * (`BALANCE_NOT_READ`): se il gateway lo dà vale quello, e questi due campi
   * si ignorano. Il motivo è obbligatorio insieme all'importo.
   */
  baseAmount?: string;
  baseNote?: string;
}

/** Un conto libero fra quelli del cliente, o uno nuovo (login, password, server). */
/**
 * Un conto libero (`accountId`) o uno nuovo. Il nuovo porta la VALUTA: `attach-broker` la richiede
 * (`currency`, 3 lettere, senza predefinito) e senza il payload veniva rifiutato — con «Invalid input»,
 * perche' era l'unica cosa che mancava a entrambi i rami dell'unione. `next-account` la ignora: il conto
 * prop nasce dalla pagina dei conti, dove la valuta si dichiara.
 */
export type AccountChoice = { accountId: string } | { mt5Login: string; mt5Password: string; serverId: string; label?: string | null; currency: string };

export type AttachBrokerInput = AccountChoice & { baseAmount?: string; baseNote?: string };

/**
 * Il payout arrivato: **obbligatorio solo il netto**.
 *
 * Lordo e percentuale della prop restano vuoti se non si conoscono:
 * uguagliarli al netto direbbe che la prop non ha trattenuto niente, che è
 * un'informazione, e falsa.
 */
export interface PayoutReceivedInput {
  netAmount: string;
  /** Vuota = la valuta del ciclo. */
  currency?: string;
  grossAmount?: string;
  /** Frazione, non percentuale: 0.8 = 80% al cliente della prop. */
  propSplitPct?: string;
  /** Le due quote confermate nell'anteprima: gli IMPORTI. */
  split?: SplitInput;
  /** Tasso payout -> reporting, e payout -> valuta del ciclo. */
  fxRate?: string;
  fxRateCycle?: string;
  /** La base del tratto nuovo, se il saldo non si legge. Col motivo. */
  baseAmount?: string;
  baseNote?: string;
  note?: string;
}

/** I parametri di UNA operazione: li sceglie l'operatore ogni volta. */
export interface OperationInput {
  direction: "LONG" | "SHORT";
  propLots: string;
  brokerMultiplier: string;
  /** Il simbolo di questa operazione; senza, quello della fase. */
  canonicalSymbol?: string;
  slMoney?: number;
  tpMoney?: number;
  slPoints?: number;
  tpPoints?: number;
}

export interface DraftPreviewInput {
  propAccountId: string;
  brokerAccountId: string;
  canonicalSymbol: string;
  accountSize: string;
  maxDdPct: string;
  maxDdBasis: string;
  dailyDdPct: string | null;
  feesPaid: string;
  startLotProp: string;
  brokerMultiplier: string;
  slPoints: number;
  tpPoints: number;
  direction?: "LONG" | "SHORT";
  targetNetGain?: string | null;
}

/** Una pagina di righe, col totale vero: la paginazione c'e' dal primo giorno. */
export interface Paged<T> {
  rows: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface OutcomeInput {
  nextPhaseFee?: string;
  nextPhaseFeeCurrency?: string;
  nextPhaseFeeFxRate?: string;
  /** Tasso valuta del broker -> reporting, solo se la chiusura lo chiede. */
  fxRate?: string;
  cycleAdjustment?: string;
  adjustmentReason?: string;
  note?: string;
  /**
   * Le due quote del netto, come le ha confermate l'operatore: gli IMPORTI,
   * non la percentuale. `pct` viaggia solo come contesto per l'audit e per la
   * nota della riga — se l'operatore ha spostato un importo, la percentuale
   * mostrata e' quella ricalcolata da lui, e il dato restano i due numeri.
   */
  split?: SplitInput;
}

export interface SplitInput {
  client: number;
  manager: number;
  pct: string;
}

/** L'anteprima della bruciatura: tutti gli importi in `currency`. */
export interface BurnPreview {
  cycleId: string;
  currency: string;
  brokerCurrency: string;
  openPairs: number;
  closedPairs: number;
  propPnl: number;
  brokerPnlNative: number;
  brokerPnl: number | null;
  feesPaid: number;
  payoutsReceived: number;
  adjustments: number;
  realRealizedPnl: number | null;
  cycleNet: number | null;
  fxMissing: boolean;
  fx: { base: string; quote: string } | null;
  burnSplitPct: number;
  split: { client: number; manager: number; pctShown: number } | null;
  /**
   * Il periodo che si chiuderebbe, con il netto dal SALDO: è quello che la
   * chiusura scriverà. `null` su un ciclo senza periodi. Gli importi sono in
   * `period.currency` (valuta del broker), tranne `netCycle` e `dealsDelta`.
   */
  period: {
    id: string;
    seq: number;
    currency: string;
    baseAmount: number;
    baseSource: BaseSource;
    cashAdjustments: number;
    closeAmount: number | null;
    closeAt: string | null;
    netNative: number | null;
    netCycle: number | null;
    /** Scarto contro il consuntivo dai deal: un dato, non un avviso. */
    dealsDelta: number | null;
  } | null;
  /** C'è un periodo aperto ma il saldo non si è letto: il netto non si stima. */
  balanceMissing: boolean;
  /** Perché il saldo non c'è: lo stesso testo che rifiuterà la chiusura. */
  balanceProblem: { code: string; message: string } | null;
}

export function errorMessage(e: unknown): string {
  return e instanceof Error ? e.message : "Operazione non riuscita";
}
